import { ReactElement } from 'react';

import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';
import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';

import { StrongText, StyledFlex, Text, TextSeparator, TextSeparatorType } from '@dreryk/react-components';
import { addressFormatter, Translate } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';

import { waitlistEntryTranslationKeys } from '../../../../translations';

import { PatientAvailabilityPreferredDates } from './PatientAvailabilityPreferredDates';
import { StyledWaitlistEntrySection } from './WaitlistEntrySection.styled';
import {
   getFormattedPatientName,
   getFormattedPatientPhoneNumber,
   getFormattedPractitionerName,
   isPatientAvailabilityWholeWeek,
} from './WaitlistEntrySection.utils';

type WaitlistEntrySectionProps = {
   readonly entry: WaitlistEntry;
   readonly patient: PatientSimple;
};

// eslint-disable-next-line max-lines-per-function
export const WaitlistEntrySection = (props: WaitlistEntrySectionProps): ReactElement => {
   const phoneNumber = getFormattedPatientPhoneNumber(props.entry, props.patient);

   return (
      <StyledWaitlistEntrySection>
         <StyledFlex direction='column' gap='8px'>
            <Translate>{waitlistEntryTranslationKeys.scheduleAppointmentFor}</Translate>
            <StrongText testId={'patientName'}>{getFormattedPatientName(props.entry, props.patient)}</StrongText>
            {phoneNumber ? <Text testId={'phoneNumber'}>{phoneNumber}</Text> : null}

            <StyledFlex direction='column' gap='4px'>
               <StrongText testId={'specialistHeader'}>
                  <TextSeparator separator={TextSeparatorType.SPACE}>
                     <Translate>{waitlistEntryTranslationKeys.specialist}</Translate>
                     {props.entry.reservationPreference === 'PRACTITIONER' ? (
                        <Translate>{waitlistEntryTranslationKeys.prioritySuffix}</Translate>
                     ) : null}
                  </TextSeparator>
               </StrongText>
               <Text testId={'practitionerName'}>{getFormattedPractitionerName(props.entry)}</Text>
            </StyledFlex>

            {protectFeature('bookingVideoConsultations') ? (
               <StyledFlex direction='column' gap='4px'>
                  <StrongText testId={'encounterModeHeader'}>
                     <Translate>{waitlistEntryTranslationKeys.encounterMode}</Translate>
                  </StrongText>
                  <Text testId={'encounterMode'}>
                     <Translate>{waitlistEntryTranslationKeys.encounterModeMap[props.entry.encounterMode]}</Translate>
                  </Text>
               </StyledFlex>
            ) : null}

            {protectFeature('searchingPrivateMedicalServices') ? (
               <StyledFlex direction='column' gap='4px'>
                  <StrongText testId={'serviceHeader'}>
                     <Translate>{waitlistEntryTranslationKeys.service}</Translate>
                  </StrongText>
                  <Text testId={'serviceName'}>
                     {props.entry.receptionService?.name ?? (
                        <Translate>{waitlistEntryTranslationKeys.notSelected}</Translate>
                     )}
                  </Text>
               </StyledFlex>
            ) : null}

            <StyledFlex direction='column' gap='4px'>
               <StrongText testId={'preferredDateHeader'}>
                  <TextSeparator separator={TextSeparatorType.SPACE}>
                     <Translate>{waitlistEntryTranslationKeys.preferredDate}</Translate>
                     {props.entry.reservationPreference === 'TIME' ? (
                        <Translate>{waitlistEntryTranslationKeys.prioritySuffix}</Translate>
                     ) : null}
                  </TextSeparator>
               </StrongText>
               {isPatientAvailabilityWholeWeek(props.entry.patientAvailability) ? (
                  <Text testId={'wholeWeek'}>
                     <Translate>{waitlistEntryTranslationKeys.wholeWeek}</Translate>
                  </Text>
               ) : (
                  <PatientAvailabilityPreferredDates patientAvailability={props.entry.patientAvailability} />
               )}
            </StyledFlex>

            <StyledFlex direction='column' gap='4px'>
               <StrongText testId={'locationHeader'}>
                  <TextSeparator separator={TextSeparatorType.SPACE}>
                     <Translate>{waitlistEntryTranslationKeys.serviceProvidingLocationAbbreviation}</Translate>
                     {props.entry.reservationPreference === 'LOCATION' ? (
                        <Translate>{waitlistEntryTranslationKeys.prioritySuffix}</Translate>
                     ) : null}
                  </TextSeparator>
               </StrongText>
               {props.entry.encounterMode === 'VIDEO_CONSULTATION' && !props.entry.location ? (
                  <Translate>{waitlistEntryTranslationKeys.anyLocation}</Translate>
               ) : (
                  <TextSeparator separator={TextSeparatorType.COMMA_SPACE}>
                     <Text testId={'locationName'}>{props.entry.location!.name}</Text>
                     <Text testId={'locationAddress'}>{addressFormatter(props.entry.location!.address)}</Text>
                  </TextSeparator>
               )}
            </StyledFlex>

            {props.entry.note ? (
               <StyledFlex direction='column' gap='4px'>
                  <StrongText testId={'noteHeader'}>
                     <Translate>{waitlistEntryTranslationKeys.note}</Translate>
                  </StrongText>
                  <Text testId={'note'}>{props.entry.note}</Text>
               </StyledFlex>
            ) : null}
         </StyledFlex>
      </StyledWaitlistEntrySection>
   );
};
